import React, { useState, useEffect, useRef } from "react";
import { PageHeader } from "@finitless/shared/components";
import ReportCard from "../reports/ReportCard";
import MonthYearPicker from "../reports/MonthYearPicker";
import {
  useDownloadExecutiveReport,
  useDownloadPerformanceReport,
} from "../../../hooks/useReports";
import { useToast, usePostHog } from "@finitless/shared/hooks";
import {
  downloadBlob,
  generateReportFilename,
  getCurrentMonth,
} from "../../../lib/reportUtils";
import { useUserStore } from "@finitless/shared/stores";
import { BusinessDropdown } from "../../ui/Business/BusinessDropdown";
import { Icon } from "../../ui/UIElements/Icon";
import { useTranslation } from "react-i18next";

const ReportsPage = () => {
  const { t } = useTranslation(["reports", "toasts"]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const currentMonth = getCurrentMonth();
    const [year, month] = currentMonth.split("-");
    return new Date(parseInt(year), parseInt(month) - 1, 1);
  });
  const toast = useToast();
  const { trackFeatureAdoption } = usePostHog();
  const user = useUserStore((state) => state.user);

  // Local state for business filter (doesn't modify global context)
  const [filterBusiness, setFilterBusiness] = useState(null);
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const businessDropdownRef = useRef(null);

  // Close business dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        businessDropdownRef.current &&
        !businessDropdownRef.current.contains(event.target)
      ) {
        setIsBusinessDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBusinessSelect = (brand) => {
    setFilterBusiness(brand);
    setIsBusinessDropdownOpen(false);
  };

  const handleClearBusinessFilter = (e) => {
    e.stopPropagation();
    setFilterBusiness(null);
  };

  // Initialize the mutation hooks
  const executiveReportMutation = useDownloadExecutiveReport();
  const performanceReportMutation = useDownloadPerformanceReport();

  // Track analytics page view
  useEffect(() => {
    if (user?.id) {
      trackFeatureAdoption.analyticsPageViewed({
        user_id: user.id,
      });
    }
  }, [user?.id, trackFeatureAdoption]);

  // Helper function to convert Date to YYYY-MM format
  const getMonthString = (date) => {
    if (!date) return getCurrentMonth();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  // Handle executive report download
  const handleExecutiveReportDownload = async () => {
    try {
      const monthString = getMonthString(selectedDate);
      const params = {
        month: monthString,
      };

      if (filterBusiness) {
        params.business_id = filterBusiness.id;
      }

      const blob = await executiveReportMutation.mutateAsync(params);

      const filename = generateReportFilename("executive", monthString);
      downloadBlob(blob, filename);

      // Track data export
      trackFeatureAdoption.dataExported({
        export_type: "executive_report",
        month: monthString,
        business_id: filterBusiness?.id || null,
        user_id: user?.id,
      });

      toast.success("Executive report downloaded successfully", {
        title: t("reports:toasts.downloadCompleteTitle"),
        description: t("reports:toasts.executiveDownloadSuccess", {
          month: monthString,
        }),
      });
    } catch (error) {
      toast.error(t("reports:toasts.executiveDownloadError"), {
        title: t("reports:toasts.downloadErrorTitle"),
        description: error.message || t("reports:toasts.genericDownloadError"),
      });
    }
  };

  // Handle performance report download
  const handlePerformanceReportDownload = async () => {
    try {
      const monthString = getMonthString(selectedDate);
      const params = {
        month: monthString,
      };

      if (filterBusiness) {
        params.business_id = filterBusiness.id;
      }

      const blob = await performanceReportMutation.mutateAsync(params);

      const filename = generateReportFilename("performance", monthString);
      downloadBlob(blob, filename);

      // Track data export
      trackFeatureAdoption.dataExported({
        export_type: "performance_report",
        month: monthString,
        business_id: filterBusiness?.id || null,
        user_id: user?.id,
      });

      toast.success("Performance report downloaded successfully", {
        title: t("reports:toasts.downloadCompleteTitle"),
        description: t("reports:toasts.performanceDownloadSuccess", {
          month: monthString,
        }),
      });
    } catch (error) {
      toast.error(t("reports:toasts.performanceDownloadError"), {
        title: t("reports:toasts.downloadErrorTitle"),
        description: error.message || t("reports:toasts.genericDownloadError"),
      });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("reports:page.title")}
        subtitle={t("reports:page.subtitle")}
      />

      {/* Month Selection */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div className="flex items-center space-x-4">
          <div className="max-w-xs flex-1">
            <MonthYearPicker
              value={selectedDate}
              onChange={setSelectedDate}
              placeholder={t("reports:monthYearPicker.placeholder")}
              label={t("reports:page.monthPickerLabel")}
              subtitle={t("reports:page.monthPickerSubtitle")}
              id="month-year-picker"
            />
          </div>
        </div>
      </div>

      {/* Business Filter */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div className="flex items-center space-x-4">
          <div className="max-w-xs flex-1">
            <label className="mb-2 block text-sm font-medium text-white">
              {t("reports:page.filters.businessLabel")}
            </label>
            <div className="relative" ref={businessDropdownRef}>
              <button
                onClick={() =>
                  setIsBusinessDropdownOpen(!isBusinessDropdownOpen)
                }
                className="hover:bg-gray-750 flex h-10 w-full min-w-[180px] items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm transition-colors"
              >
                <span className="truncate text-gray-300">
                  {filterBusiness
                    ? filterBusiness.name
                    : t("reports:page.filters.allBusinesses")}
                </span>
                <Icon
                  name={isBusinessDropdownOpen ? "ChevronUp" : "ChevronDown"}
                  className="ml-auto h-4 w-4 flex-shrink-0 text-gray-400"
                />
              </button>
              {filterBusiness && (
                <button
                  onClick={handleClearBusinessFilter}
                  className="absolute -top-2 -right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 transition-colors hover:bg-red-700"
                  title={t("reports:page.filters.clearBusiness")}
                >
                  <Icon name="X" className="h-3 w-3 text-white" />
                </button>
              )}
              <div className="absolute top-full left-0 z-50 mt-2">
                <BusinessDropdown
                  isOpen={isBusinessDropdownOpen}
                  onClose={() => setIsBusinessDropdownOpen(false)}
                  selectedBrand={filterBusiness}
                  onBrandSelect={handleBusinessSelect}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report Cards Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <ReportCard
          title={t("reports:cards.executive.title")}
          description={t("reports:cards.executive.description")}
          actionText={t("reports:cards.downloadPdf")}
          loadingText={t("reports:cards.generating")}
          onAction={handleExecutiveReportDownload}
          isLoading={executiveReportMutation.isPending}
          icon="TrendingUp"
        />

        <ReportCard
          title={t("reports:cards.performance.title")}
          description={t("reports:cards.performance.description")}
          actionText={t("reports:cards.downloadPdf")}
          loadingText={t("reports:cards.generating")}
          onAction={handlePerformanceReportDownload}
          isLoading={performanceReportMutation.isPending}
          icon="BarChart3"
        />
      </div>

      {/* Additional Information */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
        <div className="flex items-start space-x-3">
          <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-500/10">
            <div className="h-2 w-2 rounded-full bg-blue-400"></div>
          </div>
          <div>
            <h4 className="mb-1 text-sm font-medium text-white">
              {t("reports:info.title")}
            </h4>
            <p className="text-xs leading-relaxed text-gray-400">
              {t("reports:info.description")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
